import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { MerchantCell } from '@/components/shared/merchant-cell';
import { PresenceDot } from '@/components/shared/presence-indicator';
import { useUsers } from '@/hooks/use-users';
import { useAuth } from '@/hooks/use-auth';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useMerchantMap } from '@/hooks/use-merchant-map';
import { ROLE_GROUPS } from '@/types/auth';
import type { AdminUser } from '@/types/user';
import { formatDate } from '@/lib/utils/format';
import { formatRelativeTime } from '@/lib/utils/relative-time';

/** Menschenlesbarer Rollen-Name (Marken-/Rollen-Labels — sprachidentisch) */
function roleName(role: string): string {
  const labels: Record<string, string> = {
    platform_admin: 'Platform Admin',
    platform_it: 'Platform IT',
    platform_ops: 'Platform Ops',
    merchant_product_manager: 'Product Manager',
    merchant_it: 'Merchant IT',
    merchant_viewer: 'Merchant Viewer',
  };
  return labels[role] ?? role;
}

export function UsersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [tokenStack, setTokenStack] = useState<string[]>([]);
  const currentToken = tokenStack.length > 0 ? tokenStack[tokenStack.length - 1] : undefined;

  const { data, isLoading, error, refetch } = useUsers({
    limit: 50,
    paginationToken: currentToken,
  });

  // Spec 0221: Merchant-Rollen sehen ihr "Team", Platform-Rollen die "Benutzer"-Verwaltung.
  const { showMerchant } = useMerchantScope();

  // Spec 525: Verwaltung (Anlegen + Detail-Navigation) nur fuer USER_MANAGEMENT
  // (platform_admin + merchant_product_manager); platform_ops ist list-only.
  // Presence-Spalten nur fuer OPS_AND_ADMIN (platform_admin + platform_ops).
  const { hasRole } = useAuth();
  const canManage = hasRole(ROLE_GROUPS.USER_MANAGEMENT);
  const canSeePresence = hasRole(ROLE_GROUPS.OPS_AND_ADMIN);

  // Aufloesung merchantId → Customer ID / Name fuer Anzeige und Suche (#310).
  const { resolveCustomerId, resolveMerchantName } = useMerchantMap();

  // Badge-Variante + uebersetztes Label anhand Status + enabled-Flag.
  const statusBadge = (user: AdminUser) => {
    if (!user.enabled) {
      return <Badge variant="secondary">{t('status.disabled')}</Badge>;
    }
    switch (user.status) {
      case 'CONFIRMED':
        return <Badge variant="success">{t('status.active')}</Badge>;
      case 'FORCE_CHANGE_PASSWORD':
        return <Badge variant="warning">{t('status.passwordChange')}</Badge>;
      case 'RESET_REQUIRED':
        return <Badge variant="warning">{t('status.resetPending')}</Badge>;
      case 'UNCONFIRMED':
        return <Badge variant="secondary">{t('status.unconfirmed')}</Badge>;
      default:
        return <Badge variant="outline">{user.status}</Badge>;
    }
  };

  // Client-seitiger Filter: E-Mail, Customer ID und Merchant-Name
  // (bei wenigen Usern ausreichend, bei vielen Cognito Filter nutzen).
  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data.users;
    return data.users.filter((u) => {
      if (u.email.toLowerCase().includes(term)) return true;
      if (!u.merchantId) return false;
      const customerId = resolveCustomerId(u.merchantId);
      if (customerId?.toLowerCase().includes(term)) return true;
      const name = resolveMerchantName(u.merchantId);
      return name !== u.merchantId && name.toLowerCase().includes(term);
    });
  }, [data?.users, searchTerm, resolveCustomerId, resolveMerchantName]);

  const handleNextPage = useCallback((token: string) => {
    setTokenStack((prev) => [...prev, token]);
  }, []);

  const handleRowClick = useCallback(
    (sub: string) => navigate(`/users/${sub}`),
    [navigate],
  );

  return (
    <div>
      <PageHeader
        title={showMerchant ? t('list.title.users') : t('list.title.team')}
        description={showMerchant
          ? t('list.description.users')
          : t('list.description.team')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('list.refresh')}
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => navigate('/users/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {showMerchant ? t('list.newUser') : t('list.inviteTeamMember')}
              </Button>
            )}
          </div>
        }
      />

      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={5} cols={5} />
          </CardContent>
        </Card>
      )}

      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {!isLoading && !error && data && (
        <Card>
          {/* Suche: E-Mail, Kundennummer, Merchant-Name */}
          <div className="border-b px-4 py-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('list.table.email')}</TableHead>
                <TableHead>{t('list.table.role')}</TableHead>
                <TableHead>{t('list.table.customer')}</TableHead>
                {canSeePresence && <TableHead>{t('list.table.lastLogin')}</TableHead>}
                {canSeePresence && <TableHead>{t('list.table.active')}</TableHead>}
                <TableHead>{t('list.table.status')}</TableHead>
                <TableHead>{t('list.table.created')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canSeePresence ? 7 : 5} className="py-8 text-center text-muted-foreground">
                    {searchTerm
                      ? (showMerchant ? t('list.empty.noUsersSearch') : t('list.empty.noTeamSearch'))
                      : (showMerchant ? t('list.empty.noUsers') : t('list.empty.noTeam'))}
                  </TableCell>
                </TableRow>
              )}
              {filteredUsers.map((user) => (
                <TableRow
                  key={user.sub}
                  className={canManage ? 'cursor-pointer' : undefined}
                  onClick={canManage ? () => handleRowClick(user.sub) : undefined}
                >
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{roleName(user.role)}</TableCell>
                  <TableCell>
                    {user.merchantId
                      ? <MerchantCell merchantId={user.merchantId} />
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  {canSeePresence && (
                    <TableCell className="text-sm text-muted-foreground">
                      {user.lastLoginAt
                        ? <span title={formatDate(user.lastLoginAt)}>{formatRelativeTime(user.lastLoginAt)}</span>
                        : t('presence.never')}
                    </TableCell>
                  )}
                  {canSeePresence && (
                    <TableCell>
                      <PresenceDot lastActivityAt={user.lastActivityAt} />
                    </TableCell>
                  )}
                  <TableCell>{statusBadge(user)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString('de-DE')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Forward-only Pagination */}
          {data.paginationToken && (
            <div className="flex justify-end border-t px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNextPage(data.paginationToken!)}
              >
                {t('list.nextPage')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
